// members.js — member / device label resolution.
//
// Three helpers that translate a laptop_id into a human-readable
// label by walking state.people + state.members + state.deviceLabels.
// Imported by every page that shows "who edited this".

import { state } from "./state.js";
import { api } from "./api.js";
import { getWhoami } from "./whoami.js";

// memberName returns a short, ergonomic display name for a laptop_id.
// Resolution priority (label-first, per product decision 2026-06-13):
//   1. members[].label → user-set tokens.label (a human chose this
//      name explicitly, so it beats anything derived).
//   2. people[] match → email's local-part (before "@"). The data
//      links the laptop to its owner's account, so "vincent" beats a
//      bare hostname.
//   3. members[].display_name → auto-registered hostname, prettified
//      by stripping ".local" / ".lan" suffixes.
//   4. raw laptop_id as last resort.
//
// NOTE: this resolves against state.people / state.members, which are
// populated asynchronously by the bootstrap fetch. Pages that can paint
// before that fetch lands (e.g. Analytics, mounted on page-activate)
// must call ensureMemberState() first or they'll fall through to the
// raw laptop_id. See the call site in analytics.js renderAnalyticsPage.
export function memberName(laptopId) {
  if (!laptopId) return "";
  // Account ids ("user:<id>", e.g. who last changed team settings) resolve
  // to that person's email.
  if (laptopId.startsWith("user:") && state.people) {
    const person = state.people.find((p) => p && p.user_id === laptopId.slice(5));
    if (person && person.email) return person.email;
  }
  // Single lookup on the flat members[] list, reused for the label
  // (priority 1) and the display_name/raw fallbacks (priorities 3/4).
  const m =
    state.members && state.members.find((mem) => mem.laptop_id === laptopId);
  // (1) user-set label — wins outright.
  if (m && m.label) return m.label;
  // (2) user email via people[]: scan for any person whose machines
  // list includes this laptop_id.
  if (state.people) {
    for (const p of state.people) {
      if (!p || !Array.isArray(p.machines)) continue;
      if (p.machines.some((mac) => mac.laptop_id === laptopId)) {
        const email = p.email || "";
        if (email) {
          const at = email.indexOf("@");
          return at > 0 ? email.slice(0, at) : email;
        }
        break;
      }
    }
  }
  // (3) and (4): auto-registered hostname, then raw id.
  if (m) {
    if (m.display_name) return prettifyHostname(m.display_name);
    return m.laptop_id;
  }
  return laptopId;
}

// ensureMemberState lazily populates state.people / state.members from
// /v1/teams/members when they're empty. Pages that mount before the
// bootstrap fetch lands (Analytics renders on page-activate, racing the
// Promise.all in bootstrap) call this so memberName() resolves to a
// human name on the FIRST paint instead of flashing the raw laptop_id
// until the next polling tick. Idempotent: a no-op once state is
// populated, and tolerant of the solo "not_connected" shape.
let memberStateFetch = null;
// Set once a fetch returns "no team" (not_connected / empty). Without
// it, a genuinely team-less daemon re-hits /v1/teams/members on EVERY
// analytics render (one wasted request per polling tick), since state
// never populates. Recovery is safe: bootstrap's own per-tick
// /v1/teams/members fetch is the path that fills state when a team is
// later configured — at which point the populated-check below
// short-circuits and this guard is moot.
let memberStateEmpty = false;
export async function ensureMemberState() {
  // Already populated (by bootstrap or a prior call) — nothing to do.
  if (
    (state.people && state.people.length) ||
    (state.members && state.members.length)
  ) {
    return;
  }
  // A prior fetch found no team. Don't re-poll from here — bootstrap
  // owns the recovery fetch.
  if (memberStateEmpty) return;
  // Coalesce concurrent callers onto one in-flight fetch.
  if (!memberStateFetch) {
    memberStateFetch = (async () => {
      try {
        const data = await api("/v1/teams/members");
        if (data && data.error === "not_connected") {
          memberStateEmpty = true;
          return;
        }
        if (data && data.members) state.members = data.members;
        if (data && data.people) state.people = data.people;
        // Returned 200 but with nothing to resolve against — treat as
        // empty so we don't re-poll a team that simply has no rows yet.
        if (
          !(state.members && state.members.length) &&
          !(state.people && state.people.length)
        ) {
          memberStateEmpty = true;
        }
      } catch (e) {
        // Solo / no team configured — leave state as-is; memberName
        // falls back to the raw id, same as before. Mark empty so we
        // don't retry on every render.
        memberStateEmpty = true;
      } finally {
        memberStateFetch = null;
      }
    })();
  }
  await memberStateFetch;
}

// deviceLabelFor returns the best human-readable label for a chat
// row's device column. Different precedence from memberName because
// chat cards have more room — they prefer the decrypted device_hint
// from heartbeat over the auto-registered hostname.
//
// Resolution order:
//   1. Decrypted device_hint from the daemon's heartbeat (default
//      "$USER@$HOSTNAME", user-overridable via
//      `agc device name <label>`).
//   2. Member's display_name IF it looks intentionally human-set
//      (contains "@" or " "); auto-registered bare hostnames like
//      "Mac.lan" fall through to device_hint.
//   3. Prettified bare hostname as fallback.
export function deviceLabelFor(chat) {
  const lid = chat && chat.laptopID;
  if (!lid) return "";
  const m =
    state.members && state.members.find((mem) => mem.laptop_id === lid);
  const memberLabel =
    m && m.display_name ? prettifyHostname(m.display_name) : "";
  const deviceHint =
    (state.deviceLabels && state.deviceLabels[lid]) || "";
  // Prefer device_hint when the member name looks like an auto-
  // registered bare hostname (no "@" and no spaces). Otherwise
  // member name wins.
  const memberLooksAutoReg = memberLabel && !/[ @]/.test(memberLabel);
  if (memberLabel && !memberLooksAutoReg) return memberLabel;
  if (deviceHint) return deviceHint;
  return memberLabel; // bare-hostname fallback, prettier than ""
}

// prettifyHostname strips macOS Bonjour suffixes (".local", ".lan")
// from auto-registered member names. Idempotent and safe on labels
// that don't have them.
export function prettifyHostname(s) {
  return String(s || "").replace(/\.(local|lan)$/i, "");
}

// leaseMachine returns the laptop_id from a lease record: laptop_id on
// /v1/sync/state rows, LaptopID on the Overview's /v1/teams/leases rows.
export function leaseMachine(lease) {
  return lease.laptop_id || lease.LaptopID || "";
}

// myMemberName resolves the signed-in user's display name the SAME way
// resolveChatMember resolves a chat's member — by running the user's own
// laptop_id through memberName(). This matters because memberName() has a
// fallback chain (email local-part → members[].label →
// prettifyHostname(display_name)); comparing the email local-part here
// (as a prior version did) against a chat resolved via the hostname
// fallback would silently never match. Resolving both sides through
// memberName() keeps the mine-filter comparison apples-to-apples.
//
// Returns "" when we can't resolve it (anonymous/legacy session, roster
// not loaded, or no laptop maps to a member) — callers then skip the
// mine-filter rather than show an empty list.
export function myMemberName() {
  const who = (getWhoami() && getWhoami().identity) || "";
  if (!who || who === "anonymous") return "";
  for (const p of state.people || []) {
    if (p && p.user_id === who && Array.isArray(p.machines)) {
      for (const m of p.machines) {
        if (!m || !m.laptop_id) continue;
        const name = memberName(m.laptop_id);
        // memberName returns the laptop_id itself when unresolved; treat
        // that as "no match" and try the next machine.
        if (name && name !== m.laptop_id) return name;
      }
      return "";
    }
  }
  return "";
}
